import type { HapticPattern } from '@hoedokloop/platform-ports';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { usePlatform } from '../platform/platform-context';
import {
  cueDurationMs,
  hapticForCue,
  isJuiceMuted,
  isSilentJuiceRoute,
  JUICE_ASSET_URLS,
  JUICE_COOLDOWN_MS,
  JUICE_VOLUME,
  juiceAssetForCue,
  type JuiceAsset,
  type JuiceCue,
  seoulDay,
  setJuiceMuted,
} from './feedback';

type AudioContextConstructor = new () => AudioContext;

type JuiceContextValue = {
  muted: boolean;
  setMuted: (muted: boolean) => void;
  play: (cue: JuiceCue, capKey?: string) => boolean;
  haptic: (pattern: HapticPattern) => boolean;
  celebrate: (cue: JuiceCue, capKey?: string) => boolean;
};

const JuiceContext = createContext<JuiceContextValue | undefined>(undefined);

function motionReduced(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function cappedKey(cue: JuiceCue, capKey?: string): string | undefined {
  if (capKey) return capKey;
  return cue === 'success.major' ? `${cue}:${seoulDay()}` : undefined;
}

export function JuiceProvider({ children }: PropsWithChildren) {
  const { haptics } = usePlatform();
  const contextRef = useRef<AudioContext | undefined>(undefined);
  const buffersRef = useRef(new Map<JuiceAsset, AudioBuffer>());
  const loadingRef = useRef(new Set<JuiceAsset>());
  const lastPlayedRef = useRef<Partial<Record<JuiceCue, number>>>({});
  const capsRef = useRef(new Set<string>());
  const [muted, setMutedState] = useState(() => isJuiceMuted());

  const loadAsset = useCallback(async (context: AudioContext, asset: JuiceAsset) => {
    if (buffersRef.current.has(asset) || loadingRef.current.has(asset)) return;
    loadingRef.current.add(asset);
    try {
      const response = await fetch(JUICE_ASSET_URLS[asset]);
      if (!response.ok) return;
      const buffer = await context.decodeAudioData(await response.arrayBuffer());
      if (context.state !== 'closed') buffersRef.current.set(asset, buffer);
    } catch {
      // Audio asset failure must never block a study action or queue delayed playback.
    } finally {
      loadingRef.current.delete(asset);
    }
  }, []);

  const unlock = useCallback((): boolean => {
    if (isSilentJuiceRoute()) return false;
    try {
      const audioWindow = window as typeof window & {
        webkitAudioContext?: AudioContextConstructor;
      };
      const AudioContext = window.AudioContext ?? audioWindow.webkitAudioContext;
      if (!AudioContext) return false;
      const context = contextRef.current ?? new AudioContext();
      contextRef.current ??= context;
      void context.resume().catch(() => undefined);
      for (const asset of Object.keys(JUICE_ASSET_URLS) as JuiceAsset[]) {
        void loadAsset(context, asset);
      }
      return true;
    } catch {
      // Audio must never block a study action.
      return false;
    }
  }, [loadAsset]);

  useEffect(() => {
    const unlockOnGesture = () => {
      if (!unlock()) return;
      document.removeEventListener('pointerdown', unlockOnGesture);
      document.removeEventListener('keydown', unlockOnGesture);
    };
    document.addEventListener('pointerdown', unlockOnGesture, { passive: true });
    document.addEventListener('keydown', unlockOnGesture);
    return () => {
      document.removeEventListener('pointerdown', unlockOnGesture);
      document.removeEventListener('keydown', unlockOnGesture);
      void contextRef.current?.close().catch(() => undefined);
      buffersRef.current.clear();
      loadingRef.current.clear();
    };
  }, [unlock]);

  const setMuted = useCallback((next: boolean) => {
    setJuiceMuted(next);
    setMutedState(next);
  }, []);

  const acceptCue = useCallback(
    (cue: JuiceCue, capKey?: string): boolean => {
      if (
        muted ||
        isSilentJuiceRoute() ||
        motionReduced() ||
        document.visibilityState !== 'visible'
      )
        return false;
      const now = performance.now();
      if ((lastPlayedRef.current[cue] ?? -Infinity) + JUICE_COOLDOWN_MS > now) return false;
      const cap = cappedKey(cue, capKey);
      if (cap && capsRef.current.has(cap)) return false;
      lastPlayedRef.current[cue] = now;
      if (cap) capsRef.current.add(cap);
      return true;
    },
    [muted],
  );

  const playAcceptedCue = useCallback((cue: JuiceCue): boolean => {
    const context = contextRef.current;
    const buffer = buffersRef.current.get(juiceAssetForCue[cue]);
    if (!context || context.state !== 'running' || !buffer) return false;
    try {
      const source = context.createBufferSource();
      const gain = context.createGain();
      const starts = context.currentTime + 0.01;
      const ends = starts + cueDurationMs(cue) / 1_000;
      source.buffer = buffer;
      gain.gain.setValueAtTime(JUICE_VOLUME, starts);
      gain.gain.setValueAtTime(JUICE_VOLUME, Math.max(starts, ends - 0.02));
      gain.gain.linearRampToValueAtTime(0.0001, ends);
      source.connect(gain).connect(context.destination);
      source.start(starts);
      source.stop(ends);
      return true;
    } catch {
      return false;
    }
  }, []);

  const play = useCallback(
    (cue: JuiceCue, capKey?: string): boolean => {
      if (!acceptCue(cue, capKey)) return false;
      return playAcceptedCue(cue);
    },
    [acceptCue, playAcceptedCue],
  );

  const haptic = useCallback(
    (pattern: HapticPattern): boolean => haptics.trigger(pattern).ok,
    [haptics],
  );

  const celebrate = useCallback(
    (cue: JuiceCue, capKey?: string): boolean => {
      if (!acceptCue(cue, capKey)) return false;
      const played = playAcceptedCue(cue);
      const vibrated = haptic(hapticForCue[cue]);
      return played || vibrated;
    },
    [acceptCue, haptic, playAcceptedCue],
  );

  const value = useMemo<JuiceContextValue>(
    () => ({ muted, setMuted, play, haptic, celebrate }),
    [celebrate, haptic, muted, play, setMuted],
  );
  return <JuiceContext.Provider value={value}>{children}</JuiceContext.Provider>;
}

export function useJuice(): JuiceContextValue {
  const value = useContext(JuiceContext);
  if (!value) throw new Error('JuiceProvider is required');
  return value;
}

export { cueDurationMs };
